import { createContext, type ReactNode, use, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthFailureHandler, useCurrentUser, useLogout } from '@/hooks/useAuth';
import { useTranslationOverrides } from '@/hooks/useTranslationOverrides';
import { ErrorBoundary } from '@/utils/errors';
import { shouldShowAuthSplash } from '@/utils/authSessionReset';
import { isPublicUnauthedRoute } from '@/lib/routing/publicRoutes';

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  companyId: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** Session probe failed after retries (e.g. backend still cold) — keep splash + allow manual retry. */
  isBootstrapError: boolean;
  retryBootstrap: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const useAuth = () => {
  const context = use(AuthContext);
  if (!context) {
    // During HMR/Fast Refresh, context can be temporarily undefined when module identity changes.
    // Return a safe loading state so the app doesn't crash; in production we still throw.
    if (import.meta.env.DEV) {
      return {
        user: null,
        isAuthenticated: false,
        isLoading: true,
        isBootstrapError: false,
        retryBootstrap: () => {},
        logout: () => {},
      } satisfies AuthContextType;
    }
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const location = useLocation();

  if (isPublicUnauthedRoute(location.pathname)) {
    return <PublicAuthProvider>{children}</PublicAuthProvider>;
  }

  return <PrivateAuthProvider>{children}</PrivateAuthProvider>;
}

function AuthErrorBoundary({ children }: { children: ReactNode }) {
  const { t } = useTranslation();

  return (
    <ErrorBoundary
      fallback={
        <div className='min-h-screen bg-red-50 flex items-center justify-center p-4'>
          <div className='text-center'>
            <h1 className='text-2xl font-bold text-red-900 mb-2'>{t('common.generalError')}</h1>
            <p className='text-red-600 mb-4'>{t('common.authShellErrorDescription')}</p>
            <button
              type='button'
              onClick={() => window.location.reload()}
              className='bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700'
            >
              {t('errors.refreshPage')}
            </button>
          </div>
        </div>
      }
    >
      {children}
    </ErrorBoundary>
  );
}

function PublicAuthProvider({ children }: { children: ReactNode }) {
  const value = useMemo<AuthContextType>(
    () => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      isBootstrapError: false,
      retryBootstrap: () => {},
      logout: () => {},
    }),
    [],
  );

  return (
    <AuthContext.Provider value={value}>
      <AuthErrorBoundary>{children}</AuthErrorBoundary>
    </AuthContext.Provider>
  );
}

function PrivateAuthProvider({ children }: { children: ReactNode }) {
  // Session probe: keep the splash while fetching or paused (cold-start retry backoff).
  // pending+idle after an in-flight cache wipe must not block the login shell.
  const {
    data: user,
    isPending: isUserPending,
    isError: isUserError,
    isFetching: isUserFetching,
    fetchStatus,
    refetch: refetchUser,
  } = useCurrentUser();
  const logoutMutation = useLogout();

  useAuthFailureHandler();

  // Apply translation overrides based on the selected company's translation type
  useTranslationOverrides();

  const logout = useCallback(() => {
    logoutMutation.mutate();
  }, [logoutMutation]);

  const retryBootstrap = useCallback(() => {
    void refetchUser();
  }, [refetchUser]);

  const isBootstrapError = isUserError && !user && !isUserFetching;

  const value = useMemo<AuthContextType>(
    () => ({
      user: user || null,
      isAuthenticated: !!user,
      isLoading: shouldShowAuthSplash({
        isPending: isUserPending,
        fetchStatus,
        isLogoutPending: logoutMutation.isPending,
      }),
      isBootstrapError,
      retryBootstrap,
      logout,
    }),
    [user, isUserPending, fetchStatus, logoutMutation.isPending, isBootstrapError, retryBootstrap, logout],
  );

  return (
    <AuthContext.Provider value={value}>
      <AuthErrorBoundary>{children}</AuthErrorBoundary>
    </AuthContext.Provider>
  );
}

// Context hooks are typically used with components, so we'll suppress the warning
// oxlint-disable-next-line react/only-export-components
export { useAuth };
